import {
  getSelectedCustomer,
  setSelectedCustomerUseNewLandingPage,
} from '@/app/slices/userSlice';
import LogoutButton from '@/components/LogoutButton';
import { fetchInfoMessages } from '@/features/infoMessage/data';
import {
  customerIsCompany,
  userIsIndividual,
  userIsNonCustomer,
} from '@/helpers/customerUtils';
import { trackingDataEvent } from '@/helpers/trackingHelper';
import { copyToClipboard, isProductionEnvironment } from '@/helpers/utils';
import { State } from '@/types/State';
import { useQuery } from '@tanstack/react-query';
import _, { map } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProfileBadge, SidebarMenu, TextButton } from 'ui';
import { IconBuild, IconContentCopy } from 'ui/Icons';
import {
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from 'ui/mui';
import { setMenus } from '../sidebarSlice';

const SidebarContent = () => {
  const dispatch = useDispatch();
  const { menus } = useSelector((state: State) => state.ui.sidebar);
  const { selectedProperty } = useSelector((state: State) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCustomer = useSelector((state: State) =>
    getSelectedCustomer(state),
  );
  const { data: notificationsData } = useQuery({
    queryKey: ['infoMessages', selectedCustomer?.customerId],
    queryFn: () => fetchInfoMessages(selectedCustomer!.customerId),
    enabled:
      !!selectedCustomer?.customerId && !userIsNonCustomer(selectedCustomer),
    staleTime: 60 * 1000,
  });
  const { account } = useSelector((state: State) => state.user);
  const { routes } = useSelector((state: State) => state.routes);

  const setupMenus = useCallback(() => {
    const mMenus: any = {
      overview: {
        menuTitle: '',
        menuItems: [],
      },
      services: {
        menuTitle: 'Tjänster',
        menuItems: [],
      },
      tools: {
        menuTitle: 'Verktyg',
        menuItems: [],
      },
    };

    if (!routes) {
      return mMenus;
    }

    const mRoutes = [...routes];

    const { infraServices } = selectedProperty;

    _.forEach(
      _(mRoutes)
        .pickBy((r) => {
          if (r.section && !r.infraService && !r.hidden) {
            return true;
          } else {
            return (
              !!_.find(infraServices, { name: r.infraService }) && !r.hidden
            );
          }
        })
        .values()
        .uniqBy('path')
        .orderBy(['title'], ['asc'])
        .value(),
      (route) => {
        const childRoutes = _.filter(route.childRoutes, (cr) => !cr.hidden);
        const newRoute = { ...route };
        newRoute.childRoutes = childRoutes;
        mMenus[route.section].menuItems.push(newRoute);
      },
    );

    dispatch(setMenus(mMenus));
  }, [dispatch, routes, selectedProperty]);

  useEffect(() => {
    routes && setupMenus();
  }, [routes, setupMenus]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [toggled, setToggled] = useState(selectedCustomer?.useNewLandingPage);

  const switchLandingPage = () => {
    dispatch(setSelectedCustomerUseNewLandingPage(!toggled));
    setToggled(!toggled);
    trackingDataEvent(
      'overview',
      selectedCustomer?.useNewLandingPage ? 'off' : 'on',
    );
  };

  return (
    <>
      {menus &&
        map(
          {
            services: {
              menuItems: [
                ...menus.overview.menuItems,
                ...menus.services.menuItems,
                ...menus.tools.menuItems,
              ],
            },
          },
          (menu, index) => (
            <SidebarMenu
              data-clarity-unmask='True'
              items={menu.menuItems}
              key={`menu_${index}`}
              currentPath={location.pathname}
              onNavigate={(path) => navigate(path)}
            />
          ),
        )}
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: '75px' }}>
        {isMobile && !customerIsCompany(selectedCustomer) && (
          <>
            <Divider></Divider>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingLeft: 2,
                paddingTop: 3,
              }}
            >
              <Typography variant='h6'>Dashboard (valbar översikt)</Typography>
              <FormControlLabel
                sx={{ paddingLeft: 2, paddingRight: 2, width: '58px' }}
                control={
                  <Switch
                    checked={toggled}
                    onChange={() => switchLandingPage()}
                  />
                }
                label=''
              />
            </Box>
            <Box sx={{paddingLeft: 2, paddingBottom: 4}}>
              <Typography variant='body2'>
                En effektiv vy som ger dig snabb åtkomst till central
                information och viktiga åtgärder. Du kan själv välja vilken
                översikt du vill ha.
              </Typography>
            </Box>
          </>
        )}

        {!isProductionEnvironment() && (
          <>
            <TextButton
              sx={{ display: 'flex', mx: 'auto', my: 1, gap: 1 }}
              href={'/debug'}
              onClick={(e) => {
                e.preventDefault();
                navigate('/debug');
              }}
              title='Debug'
            >
              Debug
              <IconBuild />
            </TextButton>
            <TextButton
              sx={{ display: 'flex', mx: 'auto', mb: 2, gap: 1 }}
              title={`Klicka för att kopiera ${
                userIsIndividual(selectedCustomer)
                  ? 'Personnummer'
                  : 'Org.nummer'
              }`}
              onClick={() =>
                copyToClipboard(
                  selectedCustomer?.identificationNumber ||
                    account?.identificationNumber,
                )
              }
            >
              {userIsIndividual(selectedCustomer)
                ? 'Personnummer'
                : 'Org.nummer'}
              <IconContentCopy />
            </TextButton>

            <Divider />
            <Typography
              sx={{ display: 'block', ml: 2, my: 2 }}
              variant='caption'
            >
              Version: {import.meta.env.VITE_APP_ME_VERSION}
            </Typography>
          </>
        )}
      </Box>
      {selectedCustomer?.status.id === 0 ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            pb: 2,
            px: 2,
          }}
        >
          <LogoutButton fullWidth />
        </Box>
      ) : (
        <ProfileBadge
          name={selectedCustomer?.name}
          onClick={() => navigate('/profile?tab=account')}
          notificationsCount={notificationsData?.unreadMessageCount || 0}
        />
      )}
    </>
  );
};

export default SidebarContent;
